import { Injectable } from '@angular/core';

import { AppConstants } from '../app-constants.service';
import { Tuple2 } from '../model.module/tuple2.model';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';

@Injectable()
export class TokenTimer {

    private static readonly TICK: number = 15;
    private static readonly INTERVAL: number = TokenTimer.TICK * 1000;
    private static readonly GAP: number = 1000 * 60;

    private secondsIdle: number = 0;
    private maxTime: number; // = 1000 * 60 * 20; // 20 min
    private maxTimeMinutes: number;
    private refreshAfter: number; // = MAX_TIME - 1000 * 60; // 20min - 1min

    private behaviourSubject: BehaviorSubject<Tuple2<boolean, number>> = new BehaviorSubject(null);
    private observable: Observable<Tuple2<boolean, number>> = this.behaviourSubject.asObservable();

    private tokenUpdated: number;
    private waitingForUpdate: boolean = false;

    private subscription: Subscription;

    private keyupListener: any;
    private clickListener: any;
    private timerHandle: number;

    constructor(
        private appConstants: AppConstants
    ) {
        if (appConstants.tokenValidMillis) {
            this.setTokenExpirationInterval(appConstants.tokenValidMillis);
        }
    }

    setTokenExpirationInterval(millis: number): void {
        this.maxTime = millis;
        this.maxTimeMinutes = Math.round(millis / 60000);
        this.refreshAfter = millis - TokenTimer.GAP;
    }

    setTokenTimestamp(millis: number = null): void {
        this.tokenUpdated = millis || new Date().getTime();
        this.waitingForUpdate = false;
    }

    /**
     * Checks, if a given timestamp is ok. If we get a token from session storage,
     * we need to now, if it is still valid.
     *
     * @param givenTimestamp a timestamp to check.
     */
    isTimestampInRange(givenTimestamp: number): boolean {
        const now = new Date().getTime();
        return now - givenTimestamp < this.refreshAfter;
    }

    subscribe(fn: (doUpdate: Tuple2<boolean, number>) => void): void {
        this.subscription = this.observable.subscribe(fn);
    }

    unsubscribe(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
            this.subscription = null;
        }
    }

    startTimer(): void {
        if (!this.keyupListener) {
            this.keyupListener = window.addEventListener('keyup', () => {
                this.timerReset();
            });
        }

        if (!this.clickListener) {
            this.clickListener = window.addEventListener('click', () => {
                this.timerReset();
            });
        }

        if (!this.timerHandle) {
            this.timerHandle = window.setInterval(
                () => {
                    this.secondsIdle += TokenTimer.INTERVAL;
                    this.checkTime();
                },
                TokenTimer.INTERVAL
            );
        }
    }

    stopTimer(): void {
        window.removeEventListener('keyup', this.keyupListener);
        window.removeEventListener('click', this.clickListener);
        window.clearInterval(this.timerHandle);
        this.keyupListener = null;
        this.clickListener = null;
        this.timerHandle = null;
    }

    private notify(doUpdate: Tuple2<boolean, number>): void {
        this.behaviourSubject.next(doUpdate);
    }

    private checkTime(): void {
        const now = new Date().getTime();

        if (   !this.waitingForUpdate
            && this.tokenUpdated + this.refreshAfter < now
            && this.secondsIdle < this.maxTime
        ) {
            // update
            this.waitingForUpdate = true;
            this.notify(new Tuple2(true, 0));
        } else if (this.secondsIdle > this.maxTime) {
            // so we auto kill the token here
            this.waitingForUpdate = true;
            this.notify(new Tuple2(true, -1));
        } else {
            const minutesLeft = Math.round((this.maxTime - this.secondsIdle) / 60000);
            this.notify(new Tuple2(false, minutesLeft));
        }
    }

    private timerReset(): void {
        this.secondsIdle = 0;
        this.notify(new Tuple2(false, this.maxTimeMinutes));
    }

}
